import * as React from "react";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import {
  Autocomplete,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  IconButton,
  Typography,
  Tooltip,
  Grid,
  Chip,
  Checkbox,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import {
  Construction,
  CalendarMonth,
  Map,
  LocationCity,
  Gavel,
  LocalShipping,
  Straighten,
  Tag,
  AttachMoney,
  Badge,
  Tune,
  LinearScale,
  FilterCenterFocusSharp,
} from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "./style.css";
import DefaultAutocomplete from "../Autocomplete";
import DefaultDatePicker from "../DefaultDatePicker";
import { formatted, UFBR } from "../../Utils";
import { buscarCidadePorUf, buscarFontesPorTotal } from "../../Services";
import moment from "moment";
import { Percent } from "lucide-react";
import LoadingButton from "../LoadingButton";

const drawerWidth = 310;

const hoje = new Date();
const umAnoAtras = new Date();
umAnoAtras.setFullYear(hoje.getFullYear() - 1);

const FILTROS = [
  {
    name: "matServ",
    label: "Material/Servicos",
    icon: Construction,
    type: "text",
  },
  {
    name: "definicaoPrecisao",
    label: "Definir Precisão",
    icon: Tune,
    type: "checkbox",
    checked: false
  },
  {
    name: "precisaoRange",
    label: "Precisão (%)",
    icon: Percent,
    type: "range",
    min: 0,
    max: 100,
    step: 1,
    dependsOn: { field: "definicaoPrecisao", equals: true },
  },
  // {
  //   name: "pesquisaAvancada",
  //   label: "Pesquisa avançada",
  //   // icon: Construction,
  //   type: "checkbox",
  // },
  // {
  //   name: "contratoid",
  //   label: "Contrato",
  //   icon: Badge,
  //   type: "text",
  // },
  {
    name: "dataInicial",
    label: "Data inicial",
    icon: CalendarMonth,
    type: "date",
    minDate: umAnoAtras,
    maxDate: hoje,
  },
  {
    name: "dataFinal",
    label: "Data final",
    icon: CalendarMonth,
    type: "date",
        minDate: umAnoAtras,
    maxDate: hoje,
  },
  {
    name: "uf",
    label: "Estado",
    icon: Map,
    type: "tags",
    options: UFBR,
  },
  {
    name: "cidade",
    label: "Município",
    icon: LocationCity,
    type: "tags",
  },
  // { name: "orgao", label: "Orgão", icon: Gavel, type: "text" },
  {
    name: "cnpjFornecedor",
    label: "CNPJ fornecedor",
    icon: LocalShipping,
    type: "text",
  },
  { name: "unidade", label: "Unidade", icon: Straighten, type: "text" },
  { name: "marca", label: "Marca", icon: Tag, type: "text" },
  { name: "valorMin", label: "Preco mínimo", icon: AttachMoney, type: "text" },
  { name: "valorMax", label: "Preço máximo", icon: AttachMoney, type: "text" },
  {
    name: "fonte",
    label: "Fonte",
    icon: Gavel,
    type: "tags",
    options: [],
  },
];

export default function TemporaryDrawer({
  getData = async () => {},
  clearHeader = () => {},
  open,
  setOpen,
}) {
  const [filtros, setFiltros] = React.useState({
    dataInicial: moment().subtract(6, "months"),
    dataFinal: moment(),
    matServ: sessionStorage.getItem("matServ"),
    definicaoPrecisao: false,
    pesquisaAvancada: true,
  });
  const [options, setOptions] = React.useState([]);
  const [fontes, setFontes] = React.useState([]);

  const carregarFontes = async () => {
    const res = await buscarFontesPorTotal();
    if (res.success) {
      setFontes(res.data || []);
    } else {
      setFontes([]);
    }
  };

  const formatFonteNome = (nome) =>
    nome === "LICITAR DIGITAL" ? "Licitar Digital" : nome;

  // const buscarCidades = async () => {
  //   let siglas = UFBR.filter((i) => filtros?.uf.some(x=>x.sigla == i.sigla)).map((i) => i.sigla);

  //   let cidades = [];
  //   siglas.forEach(async (s) => {
  //     const res = await buscarCidadePorUf(s);

  //     if (res.success) {
  //       let value = res.data;
  //       value.forEach(x => x.Sigla = s);
  //       cidades.push(...value);
  //     }
  //   });

  //   setOptions(cidades);
  //   setFiltros({ ...filtros, cidade: [] });


  // };

const buscarCidades = async () => {
  // 1. Lista simples das siglas que o usuário QUER ver agora
  const siglasSelecionadas = filtros?.uf?.map((x) => x.sigla) || [];

  // 2. LIMPEZA: Atualiza o estado removendo cidades de UFs desmarcadas.
  // Fazemos isso primeiro para a UI reagir rápido.
  setOptions((prevOptions) => 
    prevOptions.filter(cidade => siglasSelecionadas.includes(cidade.sigla))
  );

  setFiltros((prev) => ({
    ...prev,
    cidade: (prev.cidade || []).filter(cidade => siglasSelecionadas.includes(cidade.sigla))
  }));

  // 3. VERIFICAÇÃO: Quais siglas selecionadas nós AINDA NÃO temos em memória?
  // Nota: Usamos 'options' (estado atual) para verificar o cache, pois ele ainda
  // contém as cidades antes da limpeza acima terminar de processar.
  const siglasJaNaMemoria = new Set(options.map(c => c.sigla));
  
  const siglasParaBuscar = siglasSelecionadas.filter(sigla => !siglasJaNaMemoria.has(sigla));

  // Se não tiver nada novo para buscar, paramos aqui.
  if (siglasParaBuscar.length === 0) return;

  // 4. BUSCA: Baixa apenas as novas
  const promessas = siglasParaBuscar.map(async (sigla) => {
    try {
      const res = await buscarCidadePorUf(sigla);
      if (res.success) {
        return res.data.map(cidade => ({ ...cidade, sigla: sigla }));
      }
      return [];
    } catch (error) {
      console.error(`Erro ao buscar ${sigla}`, error);
      return [];
    }
  });

  const resultados = await Promise.all(promessas);
  const novasCidades = resultados.flat();

  // 5. ADIÇÃO: Adiciona as novas cidades ao estado (que já foi limpo no passo 2)
  if (novasCidades.length > 0) {
    setOptions((prevOptions) => [...prevOptions, ...novasCidades]);

  setOptions((prevOptions) => prevOptions.sort((a, b) => a.nome.localeCompare(b.nome)));
    
  }
};

  const [pesquisando, setPesquisando] = React.useState(false);

  const handleChange = (e) => {
    let value = e.target.value;
    if (e.target.name === "cnpjFornecedor") {
      value = formatted.cnpjCpfMask(value);
    }
    if (e.target.name === "valorMin" || e.target.name === "valorMax") {
      value = formatted.moeda(value);
    }
    setFiltros({ ...filtros, [e.target.name]: value });
  };

  const limparFiltros = () => {
    setFiltros({
      dataInicial: moment().subtract(12, "months"),
      dataFinal: moment(),
      definicaoPrecisao: false
    });
  };

  const handlePesquisar = async () => {
    setPesquisando(true);
    sessionStorage.removeItem("matServ");
    clearHeader();
    await getData(filtros, false);
    setPesquisando(false);
  };

  const onPress = (e) => {
    if (e.keyCode === 13) handlePesquisar();
  };

  const toggleOpen = () => setOpen(!open);

  React.useEffect(() => {
    console.log('a');
    if (!filtros?.uf) {
      setFiltros((prev) => ({ ...prev, cidade: [] }));
      setOptions([]);
    } else {
      buscarCidades();
    }
  }, [filtros?.uf]);

  React.useEffect(() => {
    carregarFontes();
  }, []);

  return (
    <React.Fragment>
      <div
        className={open ? "float-button open" : "float-button"}
        onClick={toggleOpen}
      >
        {open ? (
          <CloseIcon sx={{ color: "#fff" }} />
        ) : (
          <ArrowForwardIosIcon sx={{ color: "#fff" }} />
        )}
      </div>
      <Drawer
        anchor={"left"}
        open={open}
        variant="persistent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            top: 72,
          },
          border: "none",
        }}
        elevation={6}
      >
        <Toolbar
          sx={{
            justifyContent: "center",
            backgroundColor: "#1976d2",
            color: "white",
            margin: 0,
          }}
        >
          <Typography
            sx={{
              mr: 2,
              ml: 1,
              display: { xs: "none", md: "flex" },
              fontWeight: 700,
              letterSpacing: ".1rem",
              color: "inherit",
              textDecoration: "none",
              alignItems: "center",
            }}
          >
            FILTROS DE PESQUISA
          </Typography>
        </Toolbar>
        <Stack
          spacing={2}
          sx={{
            height: "calc(100% - 260px)",
            overflowY: "scroll",
            overflowX: "hidden",
            "::-webkit-scrollbar": {
              width: 10,
            },
            "::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: 10,
            },
            "::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
            padding: "10px",
          }}
        >
          {FILTROS.map((item, index) => {
            if (item.type === "tags") {
              return (
                <Grid item xs={item.size} key={index}>
                  <Autocomplete
                    multiple
                    options={item.name === "fonte" ? fontes : item.options || options}
                    // freeSolo
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const nomeExibicao =
                          item.name === "fonte"
                            ? formatFonteNome(option.nome)
                            : option.nome;

                        return (
                          <Chip
                            key={index}
                            variant="outlined"
                            label={`${nomeExibicao}${item.name === "cidade" ? ` - ${option.sigla}` : ""}`}
                            {...getTagProps({ index })}
                          />
                        );
                      })
                    }
                    value={filtros?.[item.name] ?? []}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    onChange={(e, v) => {
                      setFiltros((prev) => ({ ...prev, [item.name]: v }));
                    }}
                    noOptionsText="Nenhum resultado"
                    getOptionLabel={(option) => {
                      if (item.name === "cidade") return `${option.nome} - ${option.sigla}`;
                      if (item.name === "fonte") return formatFonteNome(option.nome);
                      return option.nome;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className="default-input-padding"
                        variant="outlined"
                        size="small"
                        fullWidth
                        label={item.label}
                      />
                    )}
                  />
                </Grid>
              );
            }
            if (item.type === "autocomplete") {
              return (
                <DefaultAutocomplete
                  key={index}
                  label={item.label}
                  name={item.name}
                  value={filtros?.[item.name]}
                  icon={<item.icon />}
                  onChange={handleChange}
                  options={item.options || options}
                  onKeyUp={onPress}
                />
              );
            }
            if (item.type === "date") {
              return (
                <DefaultDatePicker
                  key={index}
                  label={item.label}
                  minDate={item.minDate}
                  maxDate={item.maxDate}
                  onChange={(e) => {
                    const _filtros = { ...filtros };

                    _filtros[item.name] = e;

                    const max = 12;

                    if (item.name == "dataInicial") {
                      const diff = Math.abs(
                        moment(e).diff(filtros?.dataFinal, "months")
                      );

                      if (
                        (moment(e).isAfter(filtros?.dataFinal) || diff > max) &&
                        !!filtros?.dataFinal
                      ) {
                        _filtros.dataFinal = moment(e).add(max, "months");
                      }
                    }

                    if (item.name == "dataFinal") {
                      const diff = Math.abs(
                        moment(e).diff(filtros?.dataInicial, "months")
                      );
                      if (
                        (moment(e).isBefore(filtros?.dataInicial) ||
                          diff > max) &&
                        !!filtros?.dataInicial
                      ) {
                        _filtros.dataInicial = moment(e).subtract(
                          max,
                          "months"
                        );
                      }
                    }

                    setFiltros(_filtros);
                  }}
                  value={filtros?.[item.name]}
                  onKeyDown={onPress}
                />
              );
            }
            if (item.type === "select") {
              return (
                <TextField
                  key={index}
                  select
                  label={item.label}
                  name={item.name}
                  onChange={handleChange}
                  value={filtros?.[item.name] || ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        style={{ marginRight: 7 }}
                        position="start"
                      >
                        <item.icon />
                      </InputAdornment>
                    ),
                  }}
                >
                  {item.options.map((i, _index) => (
                    <MenuItem key={_index} value={i.sigla}>
                      {i.nome}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }

            if(item.type == 'range') {
  if (!filtros?.definicaoPrecisao) return null;

              return (
    <div key={index} style={{ marginTop: 8 }}>
      <small>{item.label}: <b>{filtros?.[item.name] ?? 100}% da descrição</b>     
      </small>
      <Tooltip
      componentsProps={{
tooltip: {
  sx: {
    backgroundColor: '#0d4175e3'
  }
}
      }}
    title={
<div style={{ maxWidth: 260, lineHeight: 1.4 }}>
  <strong>O que é Precisão?</strong>

  <div style={{ marginTop: 8 }}>
    Define quantos dos termos da sua busca precisam aparecer nos resultados.
  </div>

  <strong style={{ display: "block",  marginTop: 12, marginBottom: 0 }}>
    Exemplos:
  </strong>

  <ul style={{ paddingLeft: 16, marginTop: 0, marginBottom: 8 }}>
    <li><b>100%</b>: todos os termos devem estar presentes (resultado mais exato)</li>
    <li><b>50%</b>: metade dos termos já é suficiente (mais resultados)</li>
  </ul>

  <div style={{ marginTop: 6 }}>
    Quanto maior a precisão, <b>menos resultados</b>, porém <b>mais relevantes</b>.
  </div>
  <div>
    Quanto menor, <b>mais resultados</b>, porém <b>menos específicos</b>.
  </div>
</div>

    }
    arrow
    placement="right"
  >
    <IconButton size="small" aria-label="Ajuda sobre precisão">
      <InfoOutlinedIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>

      <TextField
        size="small"
        variant="outlined"        
        name={item.name}
        type="range"
        sx={{
    '& .MuiOutlinedInput-input': {
      paddingRight: '0',
      marginRight: 1
    }
  }}
        value={filtros?.[item.name] ?? 100}
        onChange={(e) =>
          setFiltros({ ...filtros, [item.name]: Number(e.target.value) })
        }
        inputProps={{ min: item.min, max: item.max, step: item.step }}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment style={{ marginRight: 7 }} position="start">
              <item.icon />
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
            }

            if (item.type === "checkbox") {
  return (
    <div
      key={index}
      style={{ display: "flex", alignItems: "center", height: 20, marginTop: 4 }}
    >
      <Checkbox
        size="small"
        name={item.name}
        checked={!!filtros?.[item.name]}
        onChange={(e) =>
          setFiltros({
            ...filtros,
            [item.name]: e.target.checked,
          })
        }
      />
      <small>{item.label}</small>
    </div>
  );
} else {
              return (
                <TextField
                  key={index}
                  size="small"
                  variant="outlined"
                  label={item.label}
                  name={item.name}
                  type={item.type}
                  style={{ marginTop: index === 0 && 15 }}
                  value={filtros?.[item.name] || ""}
                  onChange={handleChange}
                  onKeyDown={onPress}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        style={{ marginRight: 7 }}
                        position="start"
                      >
                        <item.icon />
                      </InputAdornment>
                    ),
                  }}
                />
              );
            }
          })}
        </Stack>
        <Toolbar sx={{ flexDirection: "column", alignItems: "normal", pt: 2 }}>
          <Button disabled={pesquisando} variant="contained" color="primary" onClick={handlePesquisar}>
            Pesquisar
          </Button>
          <Button color="primary" onClick={limparFiltros}>
            Limpar filtros
          </Button>
        </Toolbar>
      </Drawer>
    </React.Fragment>
  );
}
